import styled from 'styled-components';
import { mq } from '../../../theme/mediaQueries';

export const StyledToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s-3);
  padding: 0 0 var(--s-4);
  margin-bottom: var(--s-4);
  border-bottom: 1px solid var(--line);
`;

export const StyledToolbarSearch = styled.div`
  flex: 1 1 256px;
  min-width: 0;
  max-width: 416px;

  ${mq.ltSm} {
    flex-basis: 100%;
    max-width: 100%;
  }
`;

export const StyledToolbarFilters = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-2);
  flex-wrap: wrap;
`;

export const StyledToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-2);
  margin-inline-start: auto;
  flex-wrap: wrap;

  ${mq.ltSm} {
    margin-inline-start: 0;
    width: 100%;

    > a:last-child,
    > button:last-child {
      flex: 1;
      justify-content: center;
    }
  }
`;

export const StyledToolbarDivider = styled.span`
  width: 1px;
  height: 24px;
  background: var(--line-2);
`;

export const StyledToolbarGhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid var(--line-2);
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-2);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out);

  &:hover:not(:disabled) {
    background: rgba(9,15,56,0.04);
    border-color: var(--muted-3);
  }
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  svg {
    width: 16px;
    height: 16px;
    color: var(--muted);
  }
`;

export const StyledToolbarIconButton = styled(StyledToolbarGhostButton)`
  width: 38px;
  padding: 0;
  justify-content: center;
`;

export const StyledToolbarCount = styled.span`
  font-size: var(--t-sm);
  font-weight: var(--weight-medium);
  color: var(--muted);
  white-space: nowrap;
  margin-inline-start: auto;
`;

export const StyledFilterChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 10px 0 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: border-color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out);

  &:hover:not(:disabled) { border-color: var(--muted-3); }
  &[aria-expanded='true'] {
    border-color: var(--brand-green);
    box-shadow: 0 0 0 3px rgba(118, 177, 89, 0.15);
  }
  &:disabled { opacity: 0.45; cursor: not-allowed; }

  .fc-icon {
    display: flex;
    align-items: center;
    color: var(--muted);
    flex-shrink: 0;
    svg {
      width: 14px; height: 14px;
      fill: none; stroke: currentColor;
      stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
    }
  }
  .fc-label { flex: 1 1 auto; text-align: left; }
  .fc-badge {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: var(--r-full);
    background: var(--paper-2);
    color: var(--muted);
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    flex-shrink: 0;
  }
  .fc-chevron {
    display: flex;
    align-items: center;
    color: var(--muted-2);
    flex-shrink: 0;
    margin-left: 2px;
    svg {
      width: 14px; height: 14px;
      fill: none; stroke: currentColor;
      stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
    }
  }
`;

export const StyledFilterDropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 180px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  box-shadow: 0 4px 6px -2px rgba(9,15,56,0.04),
    0 12px 32px -8px rgba(9,15,56,0.12);
  padding: 6px;
  z-index: 200;

  button {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 12px;
    background: transparent;
    border: 0;
    border-radius: var(--r-sm);
    font: inherit;
    font-size: var(--t-sm);
    font-weight: 500;
    color: var(--ink);
    cursor: pointer;
    text-align: left;
    transition: background var(--dur-fast) var(--ease-out);

    &:hover { background: var(--paper-2); }
    &[aria-selected='true'] {
      background: var(--brand-green-soft);
      color: var(--brand-green-ink);
    }
  }
`;

export const StyledActiveFiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: var(--s-3);
  padding-top: var(--s-3);
  border-top: 1px solid var(--line);
  flex-wrap: wrap;

  .active-eyebrow {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--muted-2);
    white-space: nowrap;
    flex-shrink: 0;
  }

  .active-count {
    margin-inline-start: auto;
    font-size: var(--t-sm);
    color: var(--muted);
    white-space: nowrap;
  }

  .clear-all-btn {
    background: transparent;
    border: 0;
    color: var(--muted);
    cursor: pointer;
    font-family: inherit;
    font-size: var(--t-sm);
    font-weight: 500;
    padding: 0 4px;
    transition: color var(--dur-fast) var(--ease-out);
    &:hover { color: var(--ink); }
  }
`;

export const StyledRemovableChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 6px 0 12px;
  border-radius: var(--r-full);
  border: 0;
  background: var(--brand-ink);
  color: var(--surface);
  font: inherit;
  font-size: var(--t-sm);
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: opacity var(--dur-fast) var(--ease-out);

  &:hover {
    opacity: 0.85;
  }

  svg {
    width: 15px;
    height: 15px;
    color: rgba(255, 255, 255, 0.7);
    flex-shrink: 0;
  }
`;
